import { bpsToPercent, percentNumberToBps } from '@/utils/bps';
/**
 * The boundary between bos-api's wire shapes and this client's domain shapes.
 *
 * **This is the only file that may mention a wire regime name or a decimal
 * percent rate.** Two representations differ across the boundary and both
 * differences are mapped here, once:
 *
 * | | wire (bos-api) | this client |
 * | --- | --- | --- |
 * | regime | `StandardLlc` | `STANDARD` |
 * | rates | decimal percent, `15.00` | basis points, `1500` |
 *
 * Letting either representation leak inwards is how a screen ends up with two
 * spellings of the same regime and a rounding bug at the third call site.
 */

import type {
    RecordTaxChangeInput,
    TaxRates,
    TaxRateSet,
    TaxRegime,
    TaxRegimeWire,
} from '@/api/models';

// ── wire shapes ─────────────────────────────────────────────────────────────

/**
 * One rate set exactly as bos-api serializes it: rates as decimal percent,
 * regime as the .NET enum name, `effectiveFrom` as `yyyy-MM-dd`.
 */
export type TaxRateSetWire = {
    id: string;
    effectiveFrom: string;
    regime: TaxRegimeWire;
    corporateIncomeTax: number;
    dividendWithholding: number;
    personalIncomeTax: number;
    employeePension: number;
    employerPension: number;
    statePension: number;
    vatStandard: number;
    note: string | null;
    createdByUserId: string | null;
    createdAt: string;
};

/** `GET /ledger/tax-settings` — wrapped so fields can be added non-breakingly. */
export type TaxSettingsHistoryWire = {
    sets: TaxRateSetWire[];
};

/** `POST /ledger/tax-settings` request body. */
export type RecordTaxChangeWire = {
    id: string;
    effectiveFrom: string;
    regime: TaxRegimeWire;
    corporateIncomeTax: number;
    dividendWithholding: number;
    personalIncomeTax: number;
    employeePension: number;
    employerPension: number;
    statePension: number;
    vatStandard: number;
    note: string | null;
    basedOnSetId: string | null;
};

// ── regime ──────────────────────────────────────────────────────────────────

/**
 * Regime → wire.
 *
 * Typed as a total `Record<TaxRegime, TaxRegimeWire>` so adding a regime to
 * {@link TaxRegime} fails the build here rather than silently sending
 * `undefined`. The inverse map is derived from this one, so the two can never
 * disagree.
 */
export const REGIME_TO_WIRE: Record<TaxRegime, TaxRegimeWire> = {
    STANDARD: 'StandardLlc',
    VIRTUAL_ZONE: 'VirtualZone',
    FREE_INDUSTRIAL_ZONE: 'FreeIndustrialZone',
    INTERNATIONAL_COMPANY: 'InternationalCompany',
};

/** Wire → regime. Derived, so it is exhaustive by construction. */
export const REGIME_FROM_WIRE = Object.fromEntries(
    (Object.entries(REGIME_TO_WIRE) as [TaxRegime, TaxRegimeWire][]).map(
        ([regime, wire]) => [wire, regime],
    ),
) as Record<TaxRegimeWire, TaxRegime>;

/**
 * Wire → regime, defensively.
 *
 * An unrecognised regime means the server grew a case this client has not been
 * taught. Falling back to `STANDARD` would quietly present a Virtual Zone
 * entity as a Standard LLC, so the caller is told instead and can surface it.
 */
export const regimeFromWire = (wire: string): TaxRegime | null =>
    REGIME_FROM_WIRE[wire as TaxRegimeWire] ?? null;

// ── rates ───────────────────────────────────────────────────────────────────

/**
 * Decimal percent → basis points.
 *
 * Rounded to the nearest whole basis point rather than truncated: `15.00 * 100`
 * is not exactly `1500` in binary floating point, and truncation would turn it
 * into `1499`. The server caps precision at two decimals, so every legitimate
 * value is exactly representable and the rounding is a float-repair, not a
 * silent loss of precision.
 */
export const percentToBps = percentNumberToBps;

/**
 * Basis points → decimal percent. Re-exported from `@/utils/bps` (BOS-162):
 * this conversion had four copies across the app, and a rate that rounds
 * differently on two screens is a tax figure nobody can reconcile.
 */
export { bpsToPercent };

// ── set mapping ─────────────────────────────────────────────────────────────

const ratesFromWire = (wire: TaxRateSetWire): TaxRates => ({
    corporateIncomeTaxBps: percentToBps(wire.corporateIncomeTax),
    dividendWithholdingTaxBps: percentToBps(wire.dividendWithholding),
    personalIncomeTaxBps: percentToBps(wire.personalIncomeTax),
    employeePensionBps: percentToBps(wire.employeePension),
    employerPensionBps: percentToBps(wire.employerPension),
    statePensionBps: percentToBps(wire.statePension),
    vatStandardBps: percentToBps(wire.vatStandard),
});

/**
 * Wire set → domain set. Returns `null` on an unmapped regime rather than
 * guessing — see {@link regimeFromWire}.
 */
export const toTaxRateSet = (wire: TaxRateSetWire): TaxRateSet | null => {
    const regime = regimeFromWire(wire.regime);
    if (regime === null) {
        return null;
    }
    return {
        id: wire.id,
        effectiveFrom: wire.effectiveFrom,
        regime,
        rates: ratesFromWire(wire),
        note: wire.note,
        createdByUserId: wire.createdByUserId,
        createdAt: wire.createdAt,
    };
};

/** Domain write → wire body. */
export const toRecordTaxChangeWire = (
    input: RecordTaxChangeInput,
): RecordTaxChangeWire => ({
    id: input.id,
    effectiveFrom: input.effectiveFrom,
    regime: REGIME_TO_WIRE[input.regime],
    corporateIncomeTax: bpsToPercent(input.rates.corporateIncomeTaxBps),
    dividendWithholding: bpsToPercent(input.rates.dividendWithholdingTaxBps),
    personalIncomeTax: bpsToPercent(input.rates.personalIncomeTaxBps),
    employeePension: bpsToPercent(input.rates.employeePensionBps),
    employerPension: bpsToPercent(input.rates.employerPensionBps),
    statePension: bpsToPercent(input.rates.statePensionBps),
    vatStandard: bpsToPercent(input.rates.vatStandardBps),
    note: input.note,
    basedOnSetId: input.basedOnSetId,
});
